import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["orientation", "focusLoop", "selectOnMove"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect, useCallback, useMemo } from 'react';
import { useControlledState, useLiveRef } from 'ariakit-utils/hooks';
import { useStorePublisher } from 'ariakit-utils/store';
import { useCollectionState } from '../collection/collection-state.js';
import { useCompositeState } from '../composite/composite-state.js';

function findEnabledTabById(items, id) {
  return items.find(function (item) {
    return item.id === id && !item.disabled && !item.dimmed;
  });
}

function findFirstEnabledTab(items) {
  return items.find(function (item) {
    return !item.disabled && !item.dimmed;
  });
}
/**
 * Provides state for the `Tab` components.
 * @example
 * ```jsx
 * const tab = useTabState();
 * <TabList state={tab}>
 *   <Tab>Tab 1</Tab>
 *   <Tab>Tab 2</Tab>
 * </TabList>
 * <TabPanel state={tab}>Panel 1</TabPanel>
 * <TabPanel state={tab}>Panel 2</TabPanel>
 * ```
 */


function useTabState(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$orientation = _ref.orientation,
      orientation = _ref$orientation === void 0 ? "horizontal" : _ref$orientation,
      _ref$focusLoop = _ref.focusLoop,
      focusLoop = _ref$focusLoop === void 0 ? true : _ref$focusLoop,
      _ref$selectOnMove = _ref.selectOnMove,
      selectOnMove = _ref$selectOnMove === void 0 ? true : _ref$selectOnMove,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useControlledState = useControlledState(props.defaultSelectedId, props.selectedId, props.setSelectedId),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      selectedId = _useControlledState2[0],
      setSelectedId = _useControlledState2[1];

  var composite = useCompositeState(_objectSpread({
    orientation: orientation,
    focusLoop: focusLoop
  }, props));
  var panels = useCollectionState();
  var compositeRef = useLiveRef(composite); // Selects the active tab when selectOnMove is true. Since we're listening to
  // the moves state, but not the activeId state, this effect will run only when
  // there's a move, which is usually triggered by moving through the tabs using
  // the keyboard.

  useEffect(function () {
    if (!selectOnMove) return;
    var _compositeRef$current = compositeRef.current,
        activeId = _compositeRef$current.activeId,
        items = _compositeRef$current.items;
    if (!activeId) return;
    var tab = findEnabledTabById(items, activeId);
    if (!tab) return;
    setSelectedId(tab.id);
  }, [composite.moves, selectOnMove, setSelectedId]); // Keep activeId in sync with selectedId.

  useEffect(function () {
    if (selectedId === compositeRef.current.activeId) return;
    composite.setActiveId(selectedId);
  }, [selectedId, composite.setActiveId]); // Automatically set selectedId if it's undefined.

  useEffect(function () {
    if (selectedId !== undefined) return; // First, we try to set selectedId based on the current active tab.

    var activeId = compositeRef.current.activeId;
    var tab = findEnabledTabById(composite.items, activeId);

    if (tab) {
      setSelectedId(activeId);
    } // If there's no active tab or the active tab is dimmed, we get the first
    // enabled tab instead.
    else {
      var firstEnabledTab = findFirstEnabledTab(composite.items);
      setSelectedId(firstEnabledTab == null ? void 0 : firstEnabledTab.id);
    }
  }, [selectedId, composite.items, setSelectedId]); // Keep panels tabIds in sync with the current tabs.

  useEffect(function () {
    if (!composite.items.length) return;
    panels.setItems(function (prevPanels) {
      var hasOrphanPanels = prevPanels.some(function (panel) {
        return !panel.tabId;
      });
      if (!hasOrphanPanels) return prevPanels;
      return prevPanels.map(function (panel, i) {
        if (panel.tabId) return panel;
        var tab = composite.items[i];
        return _objectSpread(_objectSpread({}, panel), {}, {
          tabId: tab == null ? void 0 : tab.id
        });
      });
    });
  }, [composite.items, panels.setItems]);
  var select = useCallback(function (id) {
    composite.move(id);
    setSelectedId(id);
  }, [composite.move, setSelectedId]);
  var state = useMemo(function () {
    return _objectSpread(_objectSpread({}, composite), {}, {
      selectedId: selectedId,
      setSelectedId: setSelectedId,
      select: select,
      panels: panels
    });
  }, [composite, selectedId, setSelectedId, select, panels]);
  return useStorePublisher(state);
}

export { useTabState };